/**
 * Launcher V2 PR B U3 (R4) — what `MembersPanel` should render, as a pure
 * function of the current scope and the matched-member query's state.
 *
 * DOM-free and Prisma-free on purpose, the same reason `matched-members.ts` /
 * `review-gate.ts` / `play-flow-coverage.ts` are: `bun:test` has no DOM under
 * `mini-apps/`, so any logic that lives inside a Glaze-importing `.tsx` file is
 * untestable there. Keeping the panel's whole decision here means the component
 * is a renderer of this result and nothing else.
 *
 * The decision that matters: while a scope is set, the panel must NEVER fall
 * back to the full roster. A roster shown under a "routed to <rung>" label is a
 * fabricated claim about routing — the honest answers are "still matching",
 * "couldn't match", or a real (possibly empty) matched subset.
 */

import type { RungScope } from './rung-scope-store';
import type { PlaybooksMatchedMembersLadderResponse } from './schemas';

/** The scoped matched-member query, reduced to what the decision needs. */
export interface ScopedMatchState {
  isPending: boolean;
  /**
   * True whenever a request for this query is actually in flight — the
   * initial fetch AND a background refetch (e.g. the ladder invalidation
   * `WaterfallCard`'s reorder handler triggers). Distinct from `isPending`
   * (Codex round-4 Finding #2): a refetch triggered by an invalidation keeps
   * serving the PREVIOUS response's data while it runs, so `isPending` alone
   * is `false` the whole time — checking `isFetching` too is what makes the
   * panel fail closed (show "matching…") instead of briefly re-rendering a
   * ladder computed under the stale, pre-reorder priority order.
   */
  isFetching: boolean;
  isError: boolean;
  /**
   * The scope's matched member ids, or `null` when they can't be read from the
   * response — the query hasn't resolved, or (rung scope) the ladder describes
   * a Playbook version that no longer has this rung.
   */
  matchedIds: readonly string[] | null;
}

export type MembersPanelState<M> =
  /** No scope — the A2 default, every campaign member. */
  | { kind: 'roster'; members: M[] }
  /** Scoped, matched set not known yet. */
  | { kind: 'matching' }
  /** Scoped, and the matched set could not be resolved. */
  | { kind: 'match-error' }
  /** Scoped, with the real matched subset (`members` in roster order). */
  | { kind: 'matched'; members: M[]; totalMatched: number };

/**
 * One rung's claimed members out of U2's ladder response, looked up by the
 * rung's stable `id`.
 *
 * `[]` (the rung matched nobody) and `null` (the ladder has no such rung) are
 * deliberately different answers: `[]` is a real, renderable result; `null`
 * means the ladder and the rendered waterfall have drifted apart and the panel
 * must say so rather than show an empty table or guess.
 *
 * An UNMAPPED rung is not special-cased — matching runs for it exactly as for a
 * mapped rung (plan R4/R5: unmapped-ness changes who gets a send, not who
 * matches).
 *
 * A single `.find()` on `rungId` is the whole lookup, because rung ids are
 * unique within a ladder (`PlaybookRungsSchema`) and a reorder never changes
 * them — only `position`. Nothing here needs to disambiguate: a
 * `segmentationId` can legitimately repeat across rungs (the seed's catch-all
 * borrows an earlier rung's), which is precisely why the scope carries an id
 * and not a segmentation pointer.
 *
 * STILL requires the ladder's own `playbookVersionId` to match
 * `expectedPlaybookVersionId` (Codex round-4 finding #2, unchanged) — the same
 * check `review-gate.ts#reviewMemberTally` does for `ReviewStep`. An id-keyed
 * lookup does not make this redundant: a reorder leaves every rung id in place
 * and only changes which rung claims whom, so a rung scoped from a Playbook
 * head that has since moved would otherwise resolve happily against a ladder
 * computed under the OLD priority order and report a stale claim. `null` (not
 * `expectedPlaybookVersionId`) means "no known expected version" (e.g. a caller
 * that hasn't threaded one through) and skips the check, matching
 * `reviewMemberTally`'s convention.
 */
export function matchedIdsForRungScope(
  ladder: PlaybooksMatchedMembersLadderResponse | undefined,
  rungId: string,
  expectedPlaybookVersionId: string | null,
): string[] | null {
  const index = ladderEntryIndex(ladder, rungId, expectedPlaybookVersionId);
  if (index === null) return null;
  return [...ladder!.rungs[index].matchedMemberIds];
}

/**
 * The private half of {@link matchedIdsForRungScope}'s lookup — the version
 * guard plus the id-keyed `find`, expressed as an index rather than the entry
 * itself so {@link matchedIdsForRungDraftScope} (W2) can also slice
 * `ladder.rungs` to "everything above this position" without re-deriving the
 * same guard a second time and risking the two functions quietly disagreeing
 * about when a ladder is too stale to trust. Shared, not duplicated, for the
 * same reason `rung-counts.ts`'s header gives for delegating to
 * `matchedIdsForRungScope` in the first place.
 */
function ladderEntryIndex(
  ladder: PlaybooksMatchedMembersLadderResponse | undefined,
  rungId: string,
  expectedPlaybookVersionId: string | null,
): number | null {
  if (ladder && expectedPlaybookVersionId !== null && ladder.playbookVersionId !== expectedPlaybookVersionId) {
    return null;
  }
  const index = ladder?.rungs.findIndex((rung) => rung.rungId === rungId) ?? -1;
  return index === -1 ? null : index;
}

/**
 * W2 (fix-round2-plan.md, Decision 5) — the ladder-aware answer for what
 * SAVING a rung's staged, unsaved conditions right now would actually
 * produce for THIS rung's bucket, used for both the inline "in bucket
 * (preview)" count (`QualifyStep.tsx`'s `RungRow`) and the Members panel's
 * `'rung-draft'` branch, so the two can never disagree with each other or
 * with what Save would produce (the Codex round-2 "Will Break #2" probe:
 * rung 1 claims members 0–89, a rung-2 draft matches 0–99, and the OLD
 * standalone preview reported 100 while saving produced the real 10).
 *
 * Why the union of the CLAIMED sets of every ladder rung ABOVE this one's
 * position equals "matched by any rung above" under first-match-wins: each
 * rung's `matchedMemberIds` in the ladder response is already POST-exclusion
 * (`matched-members.ts`'s evaluation removes members claimed by an earlier
 * rung before recording a later one's claim), so a member appears in exactly
 * one rung's `matchedMemberIds` — the rung that actually claimed them, i.e.
 * the FIRST rung (in ladder order) whose raw conditions matched them. A
 * member is therefore in `⋃ claimed(rungs above)` if and only if some rung
 * above this position claimed them, which is exactly the set saving would
 * have already removed before ever evaluating this rung's own conditions.
 * `draftMatchedIds \ ⋃ claimed(rungs above)` is thus exactly the bucket
 * saving these staged conditions at this rung's CURRENT ladder position would
 * produce — no server round trip needed, since both the ladder and the raw
 * draft match are already the two live React Query cache entries `RungRow`
 * and `MembersPanel` already hold (Decision 5).
 *
 * Round-3 (fix-round3-plan.md U25, Decision 9, orchestrator override
 * 2026-09-12) — SHARED SEGMENTATION is a second honest-null case, checked
 * BEFORE the catch-all branch below. `SegmentationConditionsEditor` saves
 * conditions onto the segmentation IDENTITY, not the rung, so if the draft
 * rung's `segmentationId` also appears on a rung ABOVE it in the ladder,
 * saving these staged conditions would retroactively change what that
 * higher-priority rung claims too — and this rung's own bucket is downstream
 * of that rung's claim under first-match-wins, so "the ladder above this rung
 * stays fixed" (the assumption the subtraction below relies on) no longer
 * holds. The strictly-honest rule is therefore "shared with a rung ABOVE",
 * not "shared with ANY other rung": a sharer BELOW this rung's position —
 * including the always-last, inert catch-all — cannot retroactively change
 * this rung's own already-computed bucket, so an unshared-above rung still
 * previews normally even if a later rung borrows its segmentation. The
 * seeded QA playbook is exactly this shape (`prisma/seed-playbooks.ts`:
 * `rungSmb` and `rungCatchAllV1` both reference `mappedASeg`) — under this
 * rule `rungSmb` (the earlier, ranked rung) keeps a real preview, and only
 * `rungCatchAllV1` (the later catch-all, which borrows `rungSmb`'s
 * segmentation) resolves to null. `rungDraftSegmentationIsShared` below
 * exposes this same answer to `MembersPanel` so it can render
 * `RUNG_DRAFT_SHARED_SEGMENTATION_NOTE` instead of the generic
 * "couldn't match" error for this specific, explainable case.
 *
 * Catch-all edge case (unshared): a catch-all's conditions are inert
 * (`matched-members-query.ts`'s `CATCH_ALL_FILTER` — it always matches
 * everyone not already claimed), so staging different draft conditions on an
 * UNSHARED catch-all changes nothing about what it would actually claim if
 * saved. Its preview is therefore its own ladder entry, regardless of
 * `draftMatchedIds` — the draft match query still runs (so Apply behaves
 * identically for every rung kind), its result is simply not the answer for
 * a catch-all row. A catch-all that DOES share its segmentation with a rung
 * above it falls into the shared-segmentation case above instead, since the
 * catch-all is (by construction) always last and therefore never the
 * "above" side of a share.
 *
 * `null` on the same terms `matchedIdsForRungScope` uses (no ladder, a stale
 * Playbook version, or an unknown rung id) — plus `null` whenever
 * `draftMatchedIds` itself is `null` (the live filter-match query hasn't
 * resolved yet), matching this module's `[]`-vs-`null` honesty rule.
 */
export function matchedIdsForRungDraftScope(
  ladder: PlaybooksMatchedMembersLadderResponse | undefined,
  rungId: string,
  expectedPlaybookVersionId: string | null,
  draftMatchedIds: readonly string[] | null,
): string[] | null {
  if (draftMatchedIds === null) return null;
  const index = ladderEntryIndex(ladder, rungId, expectedPlaybookVersionId);
  if (index === null) return null;
  const entry = ladder!.rungs[index];
  if (segmentationSharedAbove(ladder!, index)) return null;
  if (entry.isCatchAll) return [...entry.matchedMemberIds];
  const claimedAbove = new Set(ladder!.rungs.slice(0, index).flatMap((rung) => rung.matchedMemberIds));
  return draftMatchedIds.filter((id) => !claimedAbove.has(id));
}

/**
 * Round-3 (U25, Decision 9, orchestrator override 2026-09-12) — the single
 * place the sharing rule lives. Strictly honest: only a rung ABOVE `index`
 * (`i < index`) counts, never a rung below — a sharer below (including the
 * always-last catch-all) cannot retroactively change a bucket that was
 * already computed under the ladder ABOVE this position. One-token
 * narrowing, called out per Decision 9: switching to "shared with ANY other
 * rung" would be `i !== index` instead of `i < index` — do not switch
 * without a fresh orchestrator decision.
 */
function segmentationSharedAbove(ladder: PlaybooksMatchedMembersLadderResponse, index: number): boolean {
  const seg = ladder.rungs[index].segmentationId;
  return ladder.rungs.some((rung, i) => i < index && rung.segmentationId === seg);
}

/**
 * The sentence `MembersPanel` shows in place of the generic "couldn't work
 * out which members…" error when {@link rungDraftSegmentationIsShared} is
 * true for the previewed rung-draft — see {@link matchedIdsForRungDraftScope}'s
 * doc for why a shared-above segmentation makes the preview honest-null
 * rather than a stale or misleading number.
 */
export const RUNG_DRAFT_SHARED_SEGMENTATION_NOTE =
  "This group's segment is also used by a group above it, so saving these conditions also changes that group. The count won't be final until after you save — the preview will show then.";

/**
 * Public sibling of the private {@link segmentationSharedAbove} check, for
 * `MembersPanel` to decide whether to render {@link RUNG_DRAFT_SHARED_SEGMENTATION_NOTE}
 * instead of the generic match-error `Alert`. Answers `false` (not "unknown")
 * whenever the rung's own index can't be resolved — no ladder, a stale
 * Playbook version, or an unknown rung id — because the note only makes sense
 * as a POSITIVE, explainable answer; those other cases already render their
 * own generic error via `membersPanelState`.
 */
export function rungDraftSegmentationIsShared(
  ladder: PlaybooksMatchedMembersLadderResponse | undefined,
  rungId: string,
  expectedPlaybookVersionId: string | null,
): boolean {
  const index = ladderEntryIndex(ladder, rungId, expectedPlaybookVersionId);
  return index !== null && segmentationSharedAbove(ladder!, index);
}

export function membersPanelState<M extends { id: string }>(
  scope: RungScope,
  members: readonly M[],
  match: ScopedMatchState,
): MembersPanelState<M> {
  if (scope === null) return { kind: 'roster', members: [...members] };
  if (match.isError) return { kind: 'match-error' };
  // Codex round-4 finding #2 — `isFetching`, not just `isPending`: a
  // background refetch (e.g. the ladder invalidation a reorder triggers)
  // keeps serving the PREVIOUS response while it runs, so `isPending` alone
  // stays `false` for the whole window a stale ladder could otherwise be
  // read from. Failing closed here means the panel shows "matching…" through
  // that window instead of a result computed under the old priority order.
  if (match.isPending || match.isFetching) return { kind: 'matching' };
  if (match.matchedIds === null) return { kind: 'match-error' };

  const matched = new Set(match.matchedIds);
  return {
    kind: 'matched',
    // Roster order, not match order: the panel's rows keep the same ordering
    // whether or not a scope is set, so narrowing reads as a filter rather than
    // as the table re-sorting itself.
    members: members.filter((member) => matched.has(member.id)),
    // The matched COUNT is the query's answer, not the number of rows we could
    // render — a matched id the loaded roster doesn't have (a roster fetched
    // before a sync added rows) must not silently shrink the count.
    totalMatched: matched.size,
  };
}
